import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Briefcase, Moon, Sun, Loader2, AlertTriangle, CheckCircle2, User, Mail, Shield } from 'lucide-react';
import api from '../lib/api';

const ROLE_LABELS = {
  admin: 'Amministratore',
  amministrazione: 'Amministrazione',
  coordinator: 'Coordinatore',
  pm: 'Project Manager',
  risorsa: 'Risorsa'
};

export const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toggleTheme, isDark } = useTheme();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    password: '',
    confirm_password: ''
  });

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Token mancante. Utilizza il link di invito ricevuto.');
      setIsLoading(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await api.get(`/invitations/validate?token=${token}`);
      setInvitation(response.data);
      setError(null);
    } catch (err) {
      const message = err.response?.data?.detail || 'Errore nella validazione del token';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 8) {
      toast.error('La password deve essere di almeno 8 caratteri');
      return;
    }
    
    if (formData.password !== formData.confirm_password) {
      toast.error('Le password non coincidono');
      return;
    }
    
    if (!formData.nome.trim() || !formData.cognome.trim()) {
      toast.error('Inserisci nome e cognome');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await api.post('/invitations/accept', {
        token,
        nome: formData.nome.trim(),
        cognome: formData.cognome.trim(),
        password: formData.password,
        confirm_password: formData.confirm_password
      });
      
      toast.success('Account creato con successo!', {
        description: 'Ora puoi effettuare il login.'
      });
      
      // Redirect to login after short delay
      setTimeout(() => {
        navigate('/login');
      }, 1500);
      
    } catch (err) {
      const message = err.response?.data?.detail || 'Errore nella creazione dell\'account';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50 dark:bg-slate-950">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="mt-4 text-muted-foreground">Verifica invito in corso...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50 dark:bg-slate-950">
        <div className="absolute top-4 right-4">
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-full">
            {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
        
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <CardTitle className="text-xl font-bold">Invito Non Valido</CardTitle>
              <CardDescription className="mt-2">{error}</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="text-center">
            <Alert className="mb-4 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
              <AlertDescription className="text-amber-800 dark:text-amber-200 text-sm">
                Se ritieni ci sia un errore, contatta il tuo amministratore per ricevere un nuovo link di invito.
              </AlertDescription>
            </Alert>
            <Button variant="outline" onClick={() => navigate('/login')}>
              Torna al Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid invitation - show registration form
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50 dark:bg-slate-950" data-testid="register-page">
      <div className="absolute top-4 right-4">
        <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-full">
          {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-slate-900 dark:bg-white rounded-2xl flex items-center justify-center">
            <Briefcase className="h-8 w-8 text-white dark:text-slate-900" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold">Completa Registrazione</CardTitle>
            <CardDescription>Sei stato invitato a unirti a AGRI-GM</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Invitation details (read-only) */}
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="font-medium">{invitation.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Ruolo assegnato</p>
                <Badge variant="secondary" className="mt-1">
                  {ROLE_LABELS[invitation.role] || invitation.role}
                </Badge>
              </div>
            </div>
          </div>

          {/* Registration form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  type="text"
                  placeholder="Mario"
                  value={formData.nome}
                  onChange={(e) => handleChange('nome', e.target.value)}
                  required
                  data-testid="register-nome-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cognome">Cognome *</Label>
                <Input
                  id="cognome"
                  type="text"
                  placeholder="Rossi"
                  value={formData.cognome}
                  onChange={(e) => handleChange('cognome', e.target.value)}
                  required
                  data-testid="register-cognome-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                required
                minLength={8}
                data-testid="register-password-input"
              />
              <p className="text-xs text-muted-foreground">Minimo 8 caratteri</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Conferma Password *</Label>
              <Input
                id="confirm_password"
                type="password"
                placeholder="••••••••"
                value={formData.confirm_password}
                onChange={(e) => handleChange('confirm_password', e.target.value)}
                required
                minLength={8}
                data-testid="register-confirm-password-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isSubmitting}
              data-testid="register-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creazione account...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Crea Account
                </>
              )}
            </Button>
          </form>

          <p className="text-xs text-center text-muted-foreground">
            Hai già un account?{' '}
            <Button variant="link" className="p-0 h-auto text-xs" onClick={() => navigate('/login')}>
              Accedi
            </Button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
